import { useLazyQuery } from '@apollo/react-hooks';
import { Fade, makeStyles } from '@material-ui/core';
import { useField } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '~components/circular-progress';
import FormikInput from '~components/input/formik-input';
import { FormikSelectWithActionOptions } from '~components/react-select';
import { QUERY_STUDENT_BY_EMAIL } from '~containers/student/detail/gql';
import { GENDER } from '~graphql-types';

const useFieldAccountEmailStyles = makeStyles({
  root: {
    position: 'relative',
  },

  loading: {
    position: 'absolute',
    right: -24,
    top: 12,
  },
});

export function FieldAccountEmail({ propertyId, fieldConfig = 'email' }) {
  const { t } = useTranslation('nomination-manage-students');
  const classes = useFieldAccountEmailStyles();
  const [field, meta] = useField(fieldConfig);
  const [queryStudent, { data, loading }] = useLazyQuery(QUERY_STUDENT_BY_EMAIL, {
    fetchPolicy: 'network-only',
  });
  const getStudentUserByEmail = data?.getStudentUserByEmail;

  const handleBlur = () => {
    if (!meta.error && field.value) {
      queryStudent({ variables: { propertyId, email: field.value } });
    }
  };

  const displayEmailExistedError = useMemo(() => {
    return (
      !meta.error &&
      getStudentUserByEmail &&
      field.value === getStudentUserByEmail.studentInfo.email
    );
  }, [field.value, getStudentUserByEmail, meta.error]);

  const emailExistedError = displayEmailExistedError && t('this_account_email_already_exists');

  return (
    <div className={classes.root}>
      <div>
        <FormikInput
          fieldConfig={fieldConfig}
          placeholder={t('account_email')}
          onBlur={handleBlur}
          FieldHelperTextProps={{
            children: meta.touched && (meta.error || emailExistedError),
          }}
        />
      </div>

      <Fade in={loading}>
        <CircularProgress className={classes.loading} />
      </Fade>
    </div>
  );
}

export function FieldFirstName({ fieldConfig = 'firstName' }) {
  const { t } = useTranslation('nomination-manage-students');
  return (
    <div>
      <FormikInput fieldConfig={fieldConfig} placeholder={t('first_name')} />
    </div>
  );
}

export function FieldMiddleName({ fieldConfig = 'middleName' }) {
  const { t } = useTranslation('nomination-manage-students');
  return (
    <div>
      <FormikInput fieldConfig={fieldConfig} placeholder={t('middle_name_optional')} />
    </div>
  );
}

export function FieldLastName({ fieldConfig = 'lastName' }) {
  const { t } = useTranslation('nomination-manage-students');
  return (
    <div>
      <FormikInput fieldConfig={fieldConfig} placeholder={t('last_name')} />
    </div>
  );
}

export function FieldPartnerReferenceId(props) {
  const { fieldConfig = 'partnerReferenceId', disabled, isOptional } = props;
  const { t } = useTranslation('nomination-manage-students');

  return (
    <div>
      <FormikInput
        fieldConfig={fieldConfig}
        placeholder={t(isOptional ? 'partner_reference_id_optional' : 'partner_reference_id')}
        disabled={disabled}
      />
    </div>
  );
}

export function FieldGender(props) {
  const { fieldConfig = 'gender', disabled, isOptional } = props;
  const { t } = useTranslation(['nomination-manage-students', 'graphql']);
  const options = useMemo(
    () => Object.values(GENDER).map((value) => ({ value, label: t(`graphql:gender.${value}`) })),
    [t]
  );

  return (
    <div>
      <FormikSelectWithActionOptions
        fieldConfig={fieldConfig}
        placeholder={t(isOptional ? 'gender_optional' : 'gender')}
        options={options}
        isDisabled={disabled}
      />
    </div>
  );
}
